// Per-child progress: unlocked items, per-item spacing stats, current unit,
// session history, streak. Stored in localStorage (small JSON per child).
// Implements the engine rules: spaced review (1,3,7,14,30,60 days), mastery gate
// (accuracy >= 0.9 on two different days), and due-item selection.

import { UNITS, MAX_ORDER, unitByOrder, itemById } from './b1.js'

export const CHILDREN = [
  { id: 'amaaya', name: 'Amaaya', emoji: '🦄' },
  { id: 'agastya', name: 'Agastya', emoji: '🚀' },
]

const INTERVALS = [1, 3, 7, 14, 30, 60] // spaced-repetition schedule (days)
const KEY = (child) => `ps-progress-${child}`

export function today() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number)
  const dt = new Date(y, m - 1, d + n)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
}

export function loadProgress(child) {
  try {
    const j = JSON.parse(localStorage.getItem(KEY(child)))
    if (j && j.child_id) return normalize(j)
  } catch (_) { /* fresh */ }
  return normalize({ child_id: child })
}
function normalize(p) {
  return {
    child_id: p.child_id,
    unlocked_items: p.unlocked_items || [],
    item_stats: p.item_stats || {},   // id -> { correct, attempts, interval_idx, next_review }
    units: p.units || {},             // order -> { passedDays: [] }
    current_order: Math.min(Math.max(1, Number(p.current_order) || 1), MAX_ORDER),
    sessions: p.sessions || [],       // { date, unit, accuracy, minutes }
    streak: p.streak || 0,
    lastDay: p.lastDay || null,
    practiceMore: p.practiceMore || [], // words the tutor flagged for extra practice
  }
}
// Flag words for the "Practice more" pool (deduped).
export function addPracticeMore(p, words) {
  for (const w of words) if (!p.practiceMore.includes(w)) p.practiceMore.push(w)
}
export function saveProgress(p) {
  try { localStorage.setItem(KEY(p.child_id), JSON.stringify(p)) } catch (_) { /* ignore */ }
}

// Record one graded attempt on an item and reschedule it.
export function recordItem(p, itemId, correct) {
  const s = p.item_stats[itemId] || { correct: 0, attempts: 0, interval_idx: -1, next_review: null }
  s.attempts += 1
  if (correct) {
    s.correct += 1
    s.interval_idx = Math.min(s.interval_idx + 1, INTERVALS.length - 1)
  } else {
    s.interval_idx = 0 // a miss resets to the first interval
  }
  s.next_review = addDays(today(), INTERVALS[Math.max(0, s.interval_idx)])
  p.item_stats[itemId] = s
}

// Item ids that are due for review today (from items the child has met).
export function dueItemIds(p) {
  const t = today()
  return Object.keys(p.item_stats).filter((id) => {
    const s = p.item_stats[id]
    return s.next_review && s.next_review <= t
  })
}

// Finish a session on `unit`: log it, update streak, apply the mastery gate.
// Returns { mastered, advanced, newOrder }.
export function logSession(p, unitId, accuracy, minutes) {
  const t = today()
  if (p.lastDay !== t) { // streak: +1 for a new consecutive day, reset if a day was skipped
    p.streak = p.lastDay && addDays(p.lastDay, 1) === t ? p.streak + 1 : 1
    p.lastDay = t
  }
  p.sessions.push({ date: t, unit: unitId, accuracy: Math.round(accuracy * 100) / 100, minutes })
}

export function finishSession(p, unit, accuracy, minutes) {
  logSession(p, unit.id, accuracy, minutes)
  const t = today()
  const u = p.units[unit.order] || { passedDays: [] }
  let mastered = false, advanced = false
  if (accuracy >= unit.mastery.accuracy && !u.passedDays.includes(t)) u.passedDays.push(t)
  p.units[unit.order] = u
  if (u.passedDays.length >= unit.mastery.sessions) {
    mastered = true
    // unlock this unit's items, advance to the next unit (once)
    for (const id of unit.new_items) if (!p.unlocked_items.includes(id)) p.unlocked_items.push(id)
    // advance progression to just past the furthest mastered unit (works whether
    // the child follows order or jumps ahead from the unit grid)
    if (unit.order >= p.current_order && unit.order < MAX_ORDER) { p.current_order = unit.order + 1; advanced = true }
  }
  return { mastered, advanced, newOrder: p.current_order }
}

export const currentUnit = (p) => unitByOrder(p.current_order)
export const displayName = (child) => (CHILDREN.find((c) => c.id === child) || {}).name || child

// ---- reporting / KPIs ----
export function isMastered(p, unit) {
  if (unit.order < p.current_order) return true
  const u = p.units[unit.order]
  return !!(u && u.passedDays && u.passedDays.length >= unit.mastery.sessions)
}
export function bestAccuracy(p, unitId) {
  let best = 0
  for (const s of p.sessions) if (s.unit === unitId && s.accuracy > best) best = s.accuracy
  return best
}
export function unitStars(p, unit) {
  const played = (p.units[unit.order]?.passedDays?.length > 0) || p.sessions.some((s) => s.unit === unit.id)
  if (isMastered(p, unit)) { const a = bestAccuracy(p, unit.id); return a >= 0.75 ? 3 : 2 }
  if (!played) return 0
  const a = bestAccuracy(p, unit.id)
  return a >= 0.9 ? 3 : a >= 0.75 ? 2 : 1
}
export function kpis(p) {
  const mastered = UNITS.filter((u) => isMastered(p, u)).length
  return {
    mastered, total: UNITS.length, percent: Math.round((mastered / UNITS.length) * 100),
    streak: p.streak, learned: p.unlocked_items.length, due: dueItemIds(p).length,
    sessions: p.sessions.length, currentOrder: p.current_order,
  }
}
export function chapters(p) {
  const by = {}
  for (const u of UNITS) {
    by[u.stage] = by[u.stage] || { stage: u.stage, total: 0, mastered: 0 }
    by[u.stage].total += 1
    if (isMastered(p, u)) by[u.stage].mastered += 1
  }
  return Object.values(by).map((c) => ({ ...c, percent: Math.round((c.mastered / c.total) * 100) }))
}
export function shakyItems(p) {
  return Object.keys(p.item_stats).filter((id) => {
    const s = p.item_stats[id]
    return s.attempts >= 2 && s.correct / s.attempts < 0.8
  })
}
